import { useState, type ReactNode } from 'react';
import { CheckCircle2, History, Landmark, Loader2, PenLine, Wallet } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { supabase } from '@/lib/supabase';
import { useTranslation } from '@/hooks/useTranslation';
import type { TranslationKey } from '@/lib/i18n';

export interface DowngradeSummary {
  connections_removed: number;
  accounts: number;
  transactions: number;
  pending_settled: number;
}

/**
 * Ending bank synchronisation, told as what it keeps rather than what it takes.
 *
 * Losing a paid feature usually reads as "you are about to lose your data", and
 * here that would be false: the accounts, their balances and every transaction
 * stay. The screen says so before the user commits, and says it again after,
 * because that is the question they are actually asking.
 */
export function DisableBankSyncModal({
  open,
  syncedAccountCount,
  onClose,
  onDowngraded,
}: {
  open: boolean;
  syncedAccountCount: number;
  onClose: () => void;
  onDowngraded: (summary: DowngradeSummary) => void;
}) {
  const { t, language } = useTranslation();
  const [isWorking, setIsWorking] = useState(false);
  const [summary, setSummary] = useState<DowngradeSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  const counted = (key: TranslationKey, count: number) =>
    t(
      (count === 1 || (language === 'fr' && count === 0) ? `${key}One` : key) as TranslationKey
    ).replace('{count}', String(count));

  const disable = async () => {
    setIsWorking(true);
    setError(null);
    try {
      const { data, error: fnError } = await supabase.functions.invoke('powens-downgrade', {
        body: {},
      });
      if (fnError) throw new Error(fnError.message);
      if (data?.error) throw new Error(data.error);

      setSummary(data as DowngradeSummary);
      onDowngraded(data as DowngradeSummary);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setIsWorking(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && !isWorking && onClose()}>
      <DialogContent className="max-h-[90vh] gap-6 overflow-y-auto border-border bg-card text-card-foreground sm:max-w-md">
        {summary ? (
          <>
            <DialogHeader>
              <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-brand-50 dark:bg-brand-900/20">
                <CheckCircle2 className="h-6 w-6 text-brand-700 dark:text-brand-400" />
              </div>
              <DialogTitle className="text-center">{t('bankSync.doneTitle')}</DialogTitle>
              <DialogDescription className="text-center">
                {t('bankSync.doneDesc')}
              </DialogDescription>
            </DialogHeader>

            <DialogFooter>
              <Button className="w-full" onClick={onClose}>
                {t('bankSync.doneClose')}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>{t('bankSync.title')}</DialogTitle>
              <DialogDescription>{t('bankSync.desc')}</DialogDescription>
            </DialogHeader>

            <ul className="space-y-3 text-sm">
              <Kept icon={Wallet}>{counted('bankSync.keptAccounts', syncedAccountCount)}</Kept>
              <Kept icon={History}>{t('bankSync.keptTransactions')}</Kept>
              <Kept icon={PenLine}>{t('bankSync.keptBalance')}</Kept>
              <Kept icon={Landmark}>{t('bankSync.keptReconnect')}</Kept>
            </ul>

            {error && <p className="text-xs text-destructive">{t('bankSync.failed')}</p>}

            <DialogFooter className="gap-2 sm:justify-between sm:space-x-0">
              <Button variant="ghost" disabled={isWorking} onClick={onClose}>
                {t('bankSync.cancel')}
              </Button>
              <Button disabled={isWorking} onClick={disable}>
                {isWorking && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t('bankSync.confirm')}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Kept({ icon: Icon, children }: { icon: typeof Wallet; children: ReactNode }) {
  return (
    <li className="flex items-start gap-3">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      <span>{children}</span>
    </li>
  );
}
